/* =================================================================
   Fygr Tour Engine — portable, vanilla JS.
   Ported from proto-onboarding-module/project/tour-engine.js.

   Public API on window.FygrTour:
     FygrTour.register(id, config)
     FygrTour.start(id)
     FygrTour.next()
     FygrTour.exit({ silent })
     FygrTour.isActive()
     FygrTour.autoStart()  // reads ?tour=xxx from URL

   In Vue templates, attach attributes:
     <button data-tour-step="comptes-pill">Comptes</button>
   ================================================================= */

import router from '@/router/router';
import appBus from '@/bus/app-bus';

(function () {
  'use strict';

  const tours = new Map();
  let active = null;

  // Parcours de prise en main affiché dans le drawer de fin (checklist de
  // progression) — FALLBACK en dur uniquement (preview/debug). En prod l'app
  // pousse la vraie liste via setOnboardingSteps, avec l'état `completed` du back.
  // SOCLE : les 5 tutos de base, communs à tous les utilisateurs. Une fois les
  // 5 terminés, l'écran de fin propose la liste des modules (cf. MODULES).
  const ONBOARDING_STEPS = [
    { id: 'dashboard-welcome', label: 'Découvrir le tableau de bord', page: '/dashboard' },
    { id: 'forecast-objectives', label: 'Saisir un objectif à la main', page: '/dashboard' },
    { id: 'forecast-formulas', label: 'Construire votre prévisionnel', page: '/dashboard' },
    { id: 'forecast-ai', label: 'Laisser l’IA proposer une prévision', page: '/dashboard' },
    { id: 'categorization-overview', label: 'Catégoriser vos transactions', page: '/dashboard' },
  ];

  // Checklist « Prise en main » pilotée par le BACK. L'app pousse ici les items
  // dérivés de getStartOnboarding via FygrTour.setOnboardingSteps([...]).
  // Chaque item : { id, label, page, completed }.
  //   - completed vient de la DB → coche visuelle basée sur le serveur, pas le localStorage.
  // Tant que rien n'est poussé (null), on retombe sur ONBOARDING_STEPS + localStorage.
  let ONBOARDING_STEPS_OVERRIDE = null;

  // Modules proposés APRÈS le socle (3/3). La liste dépend des features/droits
  // de l'utilisateur → elle est poussée par l'app via `FygrTour.setModules([...])`
  // (le moteur reste agnostique). Chaque module : { id, label, body, icon, page }
  //   - id   : identifiant du tuto à lancer (doit être register())
  //   - page : route sur laquelle lancer le tuto (défaut '/dashboard')
  // Les modules dont le tuto n'est pas encore enregistré sont masqués.
  //
  // Démo : valeur par DÉFAUT = les 2 tutos « choisis » (factures + scénarios),
  // pour que « Pour aller plus loin » s'affiche même si l'écran de fin apparaît
  // sur une page où setModules() n'a pas tourné (ex. /categories à la fin du
  // socle). L'app peut toujours écraser via FygrTour.setModules([...]).
  let MODULES = [
    {
      id: 'invoices-overview',
      label: 'Comprendre les factures',
      body: 'Suivez vos échéances et leur projection dans la trésorerie.',
      icon: 'receipt_long',
      page: '/invoices',
    },
    {
      id: 'scenarios',
      label: 'Explorer les scénarios',
      body: 'Comparez plusieurs hypothèses de trésorerie.',
      icon: 'account_tree',
      page: '/dashboard',
    },
  ];

  // Pause optionnelle APRÈS une étape (via step.nextDelay en ms) : on efface le
  // surlignage et on masque la bulle un instant pour laisser voir le résultat de
  // l'action (ex. la bascule en vue tableau) avant que la bulle ne saute sur la
  // cible suivante. 0 = transition immédiate (défaut).

  const Api = {
    register(id, config) {
      tours.set(id, Object.assign({ id }, config));
    },
    // L'app pousse ici la liste des modules à proposer après le socle, déjà
    // filtrée selon les features/droits de l'utilisateur (cf. renderEndScreen).
    setModules(list) {
      MODULES = Array.isArray(list) ? list : [];
    },
    // L'app pousse ici la checklist « Prise en main » dérivée du back
    // (getStartOnboarding) : [{ id, label, page, completed }]. Le `completed`
    // vient de la DB. Passer null/[] pour revenir au socle en dur + localStorage.
    setOnboardingSteps(list) {
      ONBOARDING_STEPS_OVERRIDE = Array.isArray(list) && list.length ? list : null;
    },
    // DEBUG (console) : affiche l'écran de fin sans jouer les tutos, avec le
    // socle marqué complété (override en mémoire, aucun état local).
    //   FygrTour.previewEnd()
    //   FygrTour.previewEnd('categorization-overview')  → avec la célébration d'un tuto
    previewEnd(id) {
      if (active) Api.exit({ silent: true });
      const cfg = tours.get(id) || {
        id: id || 'preview',
        title: 'Prise en main',
        celebration: {title: 'Prise en main terminée', drawer: true},
      };
      ONBOARDING_STEPS_OVERRIDE = ONBOARDING_STEPS.map((s) => ({
        ...s,
        completed: true,
      }));
      active = {config: cfg, stepIndex: 0, nodes: {}};
      mount();
      renderEndScreen(cfg.celebration || {title: cfg.title, drawer: true});
    },
    // Affiche l'écran recap « Prise en main » selon l'ÉTAT RÉEL (steps poussés par
    // l'app + completed DB), SANS jouer de tuto ni forcer le localStorage. Le
    // recap propose alors « Prochaine étape » = 1er chapitre non complété (ordre).
    // Sert de point d'entrée « reprise » : au lieu de lancer un tuto direct, on
    // atterrit sur le recap qui propose la suite.
    showRecap() {
      if (active) Api.exit({ silent: true });
      active = {config: {id: 'recap'}, stepIndex: 0, nodes: {}};
      mount();
      renderEndScreen({
        title: 'Prise en main',
        body: 'Reprenez votre prise en main là où vous vous êtes arrêté.',
        drawer: true,
      });
    },
    // Re-render l'écran de fin (recap/célébration) s'il est affiché — appelé
    // après que le back a renvoyé la complétion à jour (completion-sync re-pousse
    // les steps). Permet à l'item qu'on vient de terminer de passer coché.
    refreshEnd() {
      if (active && active.endCel && active.nodes && active.nodes.rail) {
        renderEndScreen(active.endCel);
      }
    },
    start(id) {
      const cfg = tours.get(id);
      if (!cfg) { console.warn('[FygrTour] Unknown tour:', id); return; }
      if (active) Api.exit();
      active = { config: cfg, stepIndex: 0, nodes: {} };
      // Origine du lancement (?from=recap|guide) → détermine le comportement de
      // fin (rouvrir le recap, ou revenir sur /guide). Capturée AVANT toute
      // navigation (router.push ci-dessous efface la query).
      try {
        active.origin = new URLSearchParams(window.location.search).get('from') || null;
      } catch { active.origin = null; }
      // Navigue vers la page du tuto si on n'y est pas (lancement direct depuis
      // une autre page, ou enchaînement de tutos). Les cibles de la 1re étape
      // sont attendues via waitTimeout, le temps que la page se monte.
      if (cfg.page) {
        try {
          const current = router.currentRoute?.value?.path;
          if (current !== cfg.page) {
            Promise.resolve(router.push(cfg.page)).catch(() => {});
          }
        } catch { /* router indispo : on continue sans naviguer */ }
      }
      // Reset systématique : chaque tuto démarre d'une base propre, quel que
      // soit l'état de départ (vue consolidée, comparatif scénario actif,
      // panneaux/dropdowns ouverts…). Le Dashboard capte cet event.
      try { appBus.$emit('tour:reset-state'); } catch { /* noop */ }
      // NB : on NE pré-filtre PAS les étapes selon les cibles présentes au
      // démarrage. La partie droite du header (Export, Scénario, Paramètres…)
      // peut être montée juste après le start() → un pré-filtrage racerait le
      // rendu et supprimerait à tort des étapes. Pour les room tours
      // (interactive:false), une cible réellement absente est simplement sautée
      // au moment venu (cf. showStep), ce qui suffit.
      // Mitigation A : si mount() ou showStep() throw, on nettoie tout
      // pour ne pas laisser le dashboard gelé avec des listeners orphelins.
      try {
        mount();
        // Hook de démarrage (ex. créer une donnée "bac à sable" avant les
        // étapes). Symétrique de onExit. S'il échoue, on quitte proprement.
        if (cfg.onStart) {
          Promise.resolve(cfg.onStart()).catch((err) => {
            console.error('[FygrTour] onStart failed, auto-exit:', err);
            Api.exit({ silent: true });
          });
        }
        // Écran d'intro optionnel : explique le fonctionnement des tutos et
        // laisse l'utilisateur tout désactiver dès le départ.
        if (cfg.intro) showIntro(cfg.intro);
        else showStep(0);
      } catch (err) {
        console.error('[FygrTour] mount/start failed, auto-exit:', err);
        Api.exit({ silent: true });
      }
    },
    next() {
      if (!active) return;
      const current = active.config.steps[active.stepIndex];
      const advance = () => {
        if (!active) return;
        const i = active.stepIndex + 1;
        if (i >= active.config.steps.length) { celebrate(); return; }
        const delay = active.config.steps[active.stepIndex]?.nextDelay || 0;
        if (!delay) { showStep(i); return; }
        // Transition douce : on retire le surlignage, on masque la bulle et on
        // estompe le voile sombre pour dégager la vue du dashboard pendant la
        // pause, puis on ancre l'étape suivante.
        cleanupTargetHighlight();
        resetOverlayClip();
        const nodes = active.nodes || {};
        if (nodes.bubble) nodes.bubble.style.opacity = '0';
        if (nodes.overlay) nodes.overlay.style.opacity = '0';
        // On NE restaure PAS l'opacité ici : showStep s'en charge une fois la
        // cible trouvée (sinon on réafficherait l'ancienne bulle « collée »).
        setTimeout(() => {
          if (!active || active.stepIndex !== i - 1) return;
          showStep(i);
        }, delay);
      };
      // Hook d'action optionnel exécuté au passage à l'étape suivante
      // (créer une facture, ouvrir/fermer une popup, naviguer…). Peut être
      // asynchrone : on attend sa résolution avant d'afficher l'étape suivante.
      if (current && typeof current.onNext === 'function' && !active._advancing) {
        active._advancing = true;
        Promise.resolve(current.onNext()).catch((err) => {
          console.error('[FygrTour] step.onNext failed:', err);
        }).finally(() => {
          if (active) active._advancing = false;
          advance();
        });
        return;
      }
      advance();
    },
    exit({ silent = false } = {}) {
      if (!active) return;
      cleanupTargetHighlight();
      const onMove = active.onMove;
      const onPopState = active.onPopState;
      Object.values(active.nodes).forEach((n) => n && n.remove && n.remove());
      document.body.classList.remove('tour-active');
      document.body.classList.remove('tour-keep-sidebar');
      const cfg = active.config;
      active = null;
      if (onMove) {
        window.removeEventListener('resize', onMove);
        window.removeEventListener('scroll', onMove, { capture: true });
      }
      if (onPopState) window.removeEventListener('popstate', onPopState);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', blockOutsideClick, { capture: true });
      document.removeEventListener('mousedown', blockOutsideClick, { capture: true });
      document.removeEventListener('pointerdown', blockOutsideClick, { capture: true });
      if (!silent && cfg.onExit) cfg.onExit();
    },
    isActive() { return !!active; },
    // Saute directement à la célébration (fin), quelle que soit l'étape courante.
    finish() { if (active) celebrate(); },
    // Ré-ancre le halo + la bulle de l'étape courante sur un autre élément SANS
    // collapse de l'overlay : on garde la transition CSS (top/left/width/height)
    // pour une extension fluide du surlignage (ex. flèche → toute la plage à droite).
    retarget(el) {
      if (!active || !el || !active.nodes) return;
      const prev = active.currentTarget;
      if (prev) {
        prev.classList.remove('tour-target--active', 'is-bouncing');
        prev.style.zIndex = active.targetPrevZ || '';
        prev.style.position = active.targetPrevPos || '';
        if (active.currentClickHandler) {
          prev.removeEventListener('click', active.currentClickHandler, { capture: true });
          active.currentClickHandler = null;
        }
      }
      if (active.advancePoll) { clearInterval(active.advancePoll); active.advancePoll = null; }
      active.currentTarget = el;
      el.classList.add('tour-target--active');
      active.targetPrevZ = el.style.zIndex || '';
      active.targetPrevPos = el.style.position || '';
      if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
      el.style.zIndex = '9050';
      positionBubble();
      updateOverlayClip();
    },
    autoStart() {
      const params = new URLSearchParams(window.location.search);
      const id = params.get('tour');
      if (id && tours.has(id)) {
        setTimeout(() => Api.start(id), 50);
      }
    },
  };

  // Colle les guillemets français à leur contenu avec des espaces insécables
  // pour éviter qu'un « ou » se retrouve seul en fin de ligne.
  function nbsp(str) {
    return (str || '').replace(/«\s+/g, '« ').replace(/\s+»/g, ' »');
  }

  function showIntro(intro) {
    if (!active || !active.nodes) return;
    const card = el('div', 'tour-intro');
    card.innerHTML = `
      <div class="tour-intro__icon">
        <span class="material-symbols-outlined">school</span>
      </div>
      <div class="tour-intro__title">${nbsp(intro.title) || 'Bienvenue !'}</div>
      <div class="tour-intro__body">${nbsp(intro.body)}</div>
      ${intro.note ? `<div class="tour-intro__note">${nbsp(intro.note)}</div>` : ''}
      <div class="tour-intro__actions">
        <button class="tour-intro__start" type="button">${intro.startLabel || 'C’est parti'}</button>
        <button class="tour-intro__optout" type="button">${intro.optoutLabel || 'Ne plus afficher les tutos'}</button>
        ${intro.optoutNote ? `<div class="tour-intro__optout-note">${intro.optoutNote}</div>` : ''}
      </div>
    `;
    ['click', 'mousedown', 'pointerdown'].forEach((ev) =>
      card.addEventListener(ev, (e) => e.stopPropagation()),
    );
    document.body.appendChild(card);
    active.nodes.intro = card;
    // Pendant l'intro : logo + titre du tour + une accroche, pour que le rail
    // ne paraisse pas vide (la carte d'étape et la progression sont masquées).
    if (active.nodes.rail) {
      active.nodes.rail.classList.add('tour-rail--intro');
      const titleEl = active.nodes.rail.querySelector('.tour-rail__title');
      if (titleEl && !active.nodes.railTagline) {
        const tagline = el('p', 'tour-rail__intro-tagline');
        tagline.textContent = intro.railTagline || 'Tutoriel guidé — suivez le guide pas à pas.';
        titleEl.insertAdjacentElement('afterend', tagline);
        active.nodes.railTagline = tagline;
      }
    }
    requestAnimationFrame(() => card.classList.add('is-shown'));

    card.querySelector('.tour-intro__start').addEventListener('click', () => {
      card.remove();
      active.nodes.intro = null;
      if (active.nodes.rail) active.nodes.rail.classList.remove('tour-rail--intro');
      if (active.nodes.railTagline) {
        active.nodes.railTagline.remove();
        active.nodes.railTagline = null;
      }
      showStep(0);
    });
    card.querySelector('.tour-intro__optout').addEventListener('click', () => {
      // « Désactiver les guides automatiques » → l'app persiste dismissed=true
      // en back (cf. tours/completion-sync). Plus d'opt-out local.
      try { appBus.$emit('tour:optout'); } catch { /* noop */ }
      Api.exit({ silent: true });
    });
  }

  // Modale centrée de fin de tuto (pop-up blanche sur fond grisé) : propose
  // une ou plusieurs actions de suite (ex. connecter un outil).
  function showFinalModal(modal) {
    const actions = (modal.actions || []).map((a, i) => `
      <button class="tour-final__action" type="button" data-action-index="${i}">
        <span class="tour-final__action-icon material-symbols-outlined">${a.icon || 'bolt'}</span>
        <span class="tour-final__action-text">
          <span class="tour-final__action-label">${nbsp(a.label)}</span>
          ${a.body ? `<span class="tour-final__action-body">${nbsp(a.body)}</span>` : ''}
        </span>
        <span class="tour-final__action-arrow material-symbols-outlined">arrow_forward</span>
      </button>
    `).join('');
    const card = el('div', 'tour-intro tour-final');
    card.innerHTML = `
      <div class="tour-intro__title">${nbsp(modal.title) || 'Bravo !'}</div>
      ${modal.body ? `<div class="tour-intro__body">${nbsp(modal.body)}</div>` : ''}
      <div class="tour-final__actions">${actions}</div>
      <button class="tour-intro__optout tour-final__dismiss" type="button">${modal.dismissLabel || 'Plus tard'}</button>
    `;
    ['click', 'mousedown', 'pointerdown'].forEach((ev) =>
      card.addEventListener(ev, (e) => e.stopPropagation()),
    );
    document.body.appendChild(card);
    if (active && active.nodes) active.nodes.finalModal = card;
    requestAnimationFrame(() => card.classList.add('is-shown'));

    const close = () => {
      if (typeof modal.onDismiss === 'function') { try { modal.onDismiss(); } catch { /* noop */ } }
      Api.exit({ silent: true });
    };
    card.querySelector('.tour-final__dismiss').addEventListener('click', close);
    card.querySelectorAll('.tour-final__action').forEach((btn) => {
      btn.addEventListener('click', () => {
        const a = (modal.actions || [])[Number(btn.getAttribute('data-action-index'))];
        if (!a) return;
        if (typeof a.onSelect === 'function') { a.onSelect(); Api.exit({ silent: true }); return; }
        if (a.href) { window.location.href = a.href; }
      });
    });
  }

  function mount() {
    document.body.classList.add('tour-active');
    // Certains tours ont besoin de la sidebar de l'app (ex. switcher d'entreprise) :
    // on ne la masque pas, on pousse tout le shell à droite du rail du tuto.
    if (active.config.keepSidebar) {
      document.body.classList.add('tour-keep-sidebar');
    }
    const cfg = active.config;

    const rail = el('aside', 'tour-rail');
    rail.innerHTML = `
      <div class="tour-rail__icon">
        <svg viewBox="0 0 53 53" width="22" height="22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path class="okl-ring" d="M23.7262 0.000183105V5.26868C13.3002 6.57437 5.2344 15.4698 5.2344 26.2502C5.2344 37.0306 13.3007 45.926 23.7262 47.2317V52.5002C10.4031 51.1692 0 39.9243 0 26.2502C0 12.576 10.4031 1.33118 23.7262 0.000183105ZM29.0355 52.5002C42.3591 51.1692 52.7622 39.9243 52.7622 26.2502C52.7622 12.576 42.3591 1.33118 29.0355 0.000183105V5.26771C39.4615 6.57339 47.5278 15.4698 47.5278 26.2502C47.5278 37.0306 39.4615 45.927 29.0355 47.2327V52.5002Z" fill="#FFFFFF"/>
          <path class="okl-comet" fill-rule="evenodd" clip-rule="evenodd" d="M11.0086 20.4398L15.6311 23.1087C14.2121 27.9501 16.2196 33.3118 20.7831 35.9466C25.3466 38.5813 30.9929 37.6386 34.4761 33.9889L39.0987 36.6577C34.0939 42.7889 25.2422 44.5652 18.1659 40.4797C11.0897 36.3942 8.20127 27.8398 11.0086 20.4398ZM41.7533 32.0596C44.561 24.6593 41.6734 16.1053 34.5972 12.0198C27.5209 7.93433 18.6684 9.71013 13.6633 15.8418L18.2858 18.5106C21.7691 14.8609 27.4165 13.9182 31.98 16.553C36.5434 19.1877 38.5502 24.5489 37.1308 29.3908L41.7533 32.0596Z" fill="#FFFFFF"/>
        </svg>
      </div>
      <h2 class="tour-rail__title"></h2>
      <p class="tour-rail__step-label"></p>
      <div class="tour-rail__card">
        <div class="tour-rail__card-title"></div>
        <div class="tour-rail__card-body"></div>
      </div>
      <p class="tour-rail__progress-label">Progression</p>
      <div class="tour-rail__progress"></div>
      <div class="tour-rail__spacer"></div>
      <button class="tour-rail__exit" type="button">
        <span>Quitter le tutoriel</span>
        <span class="tour-rail__exit-x">×</span>
      </button>
    `;
    rail.querySelector('.tour-rail__title').textContent = cfg.title;
    rail.querySelector('.tour-rail__exit').addEventListener('click', () => Api.exit());
    // Force le positionnement critique inline : indépendant du SCSS, immune au cache Vite.
    rail.style.position = 'fixed';
    rail.style.top = '0';
    rail.style.left = '0';
    rail.style.bottom = '0';
    rail.style.width = '260px';
    rail.style.zIndex = '9000';
    rail.style.background = '#3D0A37';
    rail.style.color = '#FFFFFF';
    document.body.appendChild(rail);

    // Overlay = spotlight body-level : transparent + box-shadow géante qui dimme,
    // + border aubergine qui forme l'anneau. Placé hors de tout overflow:hidden.
    const overlay = el('div', 'tour-overlay');
    overlay.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'width:0',
      'height:0',
      'background:transparent',
      'box-shadow:0 0 0 9999px rgba(0,0,0,0.5)',
      'border:2px solid #3D0A37',
      'border-radius:0',
      'outline:0 solid rgba(61,10,55,0)',
      'outline-offset:2px',
      'animation:tourHalo 1800ms ease-in-out infinite',
      'z-index:8900',
      'pointer-events:none',
      'box-sizing:border-box',
      'transition:top 200ms ease, left 200ms ease, width 200ms ease, height 200ms ease, border-radius 200ms ease',
    ].join(';') + ';';
    document.body.appendChild(overlay);

    const bubble = el('div', 'tour-bubble');
    bubble.innerHTML = `
      <div class="tour-bubble__caret"></div>
      <div class="tour-bubble__head">
        <span class="tour-bubble__badge"></span>
      </div>
      <div class="tour-bubble__title"></div>
      <div class="tour-bubble__body"></div>
      <div class="tour-bubble__foot">
        <div class="tour-bubble__dots"></div>
        <button class="tour-bubble__next" type="button" style="display:none">Suivant</button>
      </div>
    `;
    // Force position + z-index inline pour garantir le stacking au-dessus de l'overlay.
    bubble.style.position = 'fixed';
    bubble.style.zIndex = '9100';
    bubble.querySelector('.tour-bubble__next').addEventListener('click', onNextButton);
    document.body.appendChild(bubble);

    active.nodes = { rail, overlay, bubble };

    // Empêche les interactions sur l'UI du tuto de remonter jusqu'au document :
    // sinon elles déclenchent les handlers « clic/mousedown en dehors » de l'app
    // (la popup facture ferme via v-click-outside, les dropdowns ferment sur
    // mousedown…). Les handlers internes (Suivant, Quitter…) se déclenchent
    // avant (éléments plus profonds), donc rien n'est cassé.
    [rail, bubble].forEach((n) => {
      ['click', 'mousedown', 'pointerdown'].forEach((ev) =>
        n.addEventListener(ev, (e) => e.stopPropagation()),
      );
    });

    const onMove = () => { positionBubble(); updateOverlayClip(); };
    window.addEventListener('resize', onMove, { passive: true });
    window.addEventListener('scroll', onMove, { passive: true, capture: true });
    active.onMove = onMove;
    // Retour / avance navigateur (popstate) en plein tuto : navigation SPA sans
    // rechargement → sinon l'overlay resterait collé sur la nouvelle page. On
    // quitte proprement (retrait des nodes + onExit = nettoyage des objets démo).
    const onPopState = () => { if (active) Api.exit(); };
    window.addEventListener('popstate', onPopState);
    active.onPopState = onPopState;
    document.addEventListener('keydown', onKey);
    // Bloque tous les clics hors de la cible et de l'UI tour, pour que
    // l'utilisateur ne puisse interagir qu'avec l'élément visé.
    document.addEventListener('click', blockOutsideClick, { capture: true });
    document.addEventListener('mousedown', blockOutsideClick, { capture: true });
    document.addEventListener('pointerdown', blockOutsideClick, { capture: true });
  }

  function onKey(e) { if (e.key === 'Escape' && active) Api.exit(); }

  function blockOutsideClick(e) {
    if (!active) return;
    const t = e.target;
    // Les clics sur l'UI du tuto (rail, bulle + « Suivant », FAB, intro) sont
    // TOUJOURS autorisés — avant tout autre traitement.
    if (t.closest('.tour-rail, .tour-bubble, .tour-skip-fab, .tour-intro')) return;
    // Les overlays de dropdown FYGR (+ zones de sélection texte) sont autorisés
    // par défaut (choisir une option / surligner) — SAUF si l'étape est en
    // strictTarget : là seule la cible surlignée reste cliquable (ex. n'autoriser
    // que « Ajouter une entreprise » dans le dropdown, pas les autres sociétés).
    const stepNow = active.config.steps[active.stepIndex];
    if (!(stepNow && stepNow.strictTarget) &&
        t.closest('.f-dropdown-overlay, .tour-allow-select')) return;
    // Mitigation B : si la cible a été retirée du DOM (Vue re-render, navigation
    // côté composant, etc.), on quitte le tour proprement plutôt que de geler
    // la page sans cible cliquable possible.
    if (active.currentTarget && !document.body.contains(active.currentTarget)) {
      Api.exit({ silent: true });
      return;
    }
    const step = active.config.steps[active.stepIndex];
    if (active.currentTarget && (active.currentTarget === t || active.currentTarget.contains(t))) {
      // blockWithin : éléments à NEUTRALISER même s'ils sont dans la cible (ex.
      // la croix de fermeture du popup, qui casserait le tuto).
      if (step && step.blockWithin && t.closest(step.blockWithin)) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        return;
      }
      // allowWithin : dans la cible, SEULS les éléments correspondants restent
      // cliquables ; tout le reste de la cible est neutralisé (ex. cibler la
      // cellule entière mais n'autoriser que le clic sur la baguette IA).
      if (step && step.allowWithin && !t.closest(step.allowWithin)) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        return;
      }
      // lockTarget : on surligne la cible sans la rendre cliquable (étape
      // purement explicative — ex. la ligne d'une facture qu'on ne veut pas
      // ouvrir en cliquant).
      if (!(step && step.lockTarget)) return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
  }

  // Fait pivoter le logo d'un cran à chaque étape : l'anneau et la comète
  // tournent en sens inverse (transition CSS gère le mouvement).
  function spinLogo() {
    if (!active || !active.nodes || !active.nodes.rail) return;
    active.logoAngle = (active.logoAngle || 0) + 40;
    const svg = active.nodes.rail.querySelector('.tour-rail__icon svg');
    if (!svg) return;
    const ring = svg.querySelector('.okl-ring');
    const comet = svg.querySelector('.okl-comet');
    if (ring) ring.style.transform = `rotate(${active.logoAngle}deg)`;
    if (comet) comet.style.transform = `rotate(${-active.logoAngle}deg)`;
  }

  function showStep(idx) {
    if (!active) return;
    active.stepIndex = idx;
    spinLogo();
    const step = active.config.steps[idx];
    if (!step) return;

    cleanupTargetHighlight();

    // La cible peut être dynamique (sélecteur dépendant d'une donnée créée à
    // la volée) : on accepte une string ou une fonction renvoyant une string.
    // On RE-résout à chaque tick : une cible dynamique peut renvoyer null tant
    // que le DOM n'est pas prêt (table en cours de rendu, ligne virtualisée pas
    // encore montée…) — sinon on échouerait au tout premier essai.
    const timeout = step.waitTimeout || 1500;
    // On capture l'instance active courante : une résolution tardive issue d'un
    // run précédent (poll orphelin) ne doit JAMAIS piloter le run en cours. Sans
    // ça, un tick resté vivant après un exit peut peindre l'étape 1 par-dessus
    // l'intro du nouveau run (les deux sont à stepIndex 0) et re-seeder la démo.
    const runToken = active;
    waitForResolvedTarget(step.target, timeout, () => active === runToken).then((target) => {
      if (active !== runToken || active.stepIndex !== idx) return;
      // Ne jamais peindre une étape tant que l'intro du run est affichée.
      if (active.nodes && active.nodes.intro) return;
      if (!target) {
        console.warn('[FygrTour] Target not found:', resolveSelector(step.target));
        // En room tour, une cible absente (bouton masqué par permissions /
        // feature flag) ne doit pas figer la visite : on saute au pas suivant.
        if (active.config.interactive === false) {
          if (idx >= active.config.steps.length - 1) celebrate();
          else showStep(idx + 1);
          return;
        }
        // Tour interactif : si la cible reste introuvable, ne JAMAIS laisser
        // l'utilisateur piégé (overlay + bloqueur de clics actifs sans bulle).
        // On quitte proprement le tuto.
        Api.exit({ silent: true });
        return;
      }
      active.currentTarget = target;
      // Cible trouvée : on réaffiche la bulle / le voile (masqués pendant une
      // éventuelle pause nextDelay).
      if (active.nodes.bubble) active.nodes.bubble.style.opacity = '';
      if (active.nodes.overlay) active.nodes.overlay.style.opacity = '';
      target.classList.add('tour-target--active');
      // Sauvegarde + force le stacking au-dessus de l'overlay
      active.targetPrevZ = target.style.zIndex || '';
      active.targetPrevPos = target.style.position || '';
      if (getComputedStyle(target).position === 'static') target.style.position = 'relative';
      target.style.zIndex = '9050';

      // Avance au clic sur la cible (étape interactive) ou via « Suivant »
      // (étape explicative). Réglable par étape via clickToAdvance, défaut =
      // mode du tour (interactive).
      if (stepClickToAdvance(step) && !step.advanceWhen) {
        target.addEventListener('click', onTargetClick, { capture: true, once: false });
        active.currentClickHandler = onTargetClick;
      }

      // Avance automatique à l'apparition d'un élément (sans clic sur la cible) :
      // utile quand l'utilisateur doit accomplir une action libre dans la zone
      // surlignée (ex. nommer + créer la formule) avant de passer à la suite.
      if (step.advanceWhen) {
        const poll = setInterval(() => {
          if (!active || active.stepIndex !== idx) { clearInterval(poll); return; }
          if (document.querySelector(resolveSelector(step.advanceWhen))) {
            clearInterval(poll);
            active.advancePoll = null;
            Api.next();
          }
        }, 200);
        active.advancePoll = poll;
      }

      // Hook optionnel `onShow(target)` : appelé une fois la cible trouvée et
      // surlignée. Peut renvoyer une fonction de cleanup (retirée au changement
      // d'étape). Utile pour intercepter un événement sur la cible (ex. neutraliser
      // le vrai submit d'un bouton pendant la démo).
      if (typeof step.onShow === 'function') {
        try {
          const cleanup = step.onShow(target);
          if (typeof cleanup === 'function') active.currentShowCleanup = cleanup;
        } catch (err) {
          console.error('[FygrTour] step.onShow failed:', err);
        }
      }

      updateRail();
      updateBubble();
      try { target.scrollIntoView({ block: 'nearest', behavior: 'instant' }); } catch { /* noop */ }
      // Mesure différée : on attend que les animations CSS (expandY 0.25s sur les
      // dropdowns FYGR, etc.) soient terminées avant de calculer le spotlight.
      const measure = () => {
        if (!active || active.currentTarget !== target) return;
        positionBubble();
        updateOverlayClip();
      };
      requestAnimationFrame(measure);
      setTimeout(measure, 320);
    });
  }

  function updateOverlayClip() {
    if (!active || !active.currentTarget || !active.nodes) return;
    const r = active.currentTarget.getBoundingClientRect();
    const cs = getComputedStyle(active.currentTarget);
    const baseRadius = cs.borderRadius && cs.borderRadius !== '0px' ? cs.borderRadius : '6px';
    // Petit gap entre la cible et l'anneau (similaire à un focus-ring offset).
    const pad = 3;
    const o = active.nodes.overlay;
    o.style.top = `${r.top - pad}px`;
    o.style.left = `${r.left - pad}px`;
    o.style.width = `${r.width + pad * 2}px`;
    o.style.height = `${r.height + pad * 2}px`;
    o.style.borderRadius = baseRadius;
  }

  function resetOverlayClip() {
    if (!active || !active.nodes || !active.nodes.overlay) return;
    const o = active.nodes.overlay;
    o.style.top = '0';
    o.style.left = '0';
    o.style.width = '0';
    o.style.height = '0';
    o.style.borderRadius = '0';
  }

  function resolveSelector(target) {
    try { return typeof target === 'function' ? target() : target; }
    catch { return null; }
  }

  // Une étape avance-t-elle au clic sur la cible (true) ou via « Suivant » ?
  function stepClickToAdvance(step) {
    if (step && typeof step.clickToAdvance === 'boolean') return step.clickToAdvance;
    return active.config.interactive !== false;
  }

  // Clic sur « Suivant » : on reproduit exactement l'action attendue de
  // l'étape. Si l'étape s'active au clic sur la cible, on clique la cible pour
  // déclencher le vrai comportement de l'app (puis l'avancement se fait via le
  // handler de clic / le waitFor). Sinon on passe à l'étape suivante (qui
  // exécute l'éventuel onNext).
  function onNextButton() {
    if (!active) return;
    const step = active.config.steps[active.stepIndex];
    // Étape avec onNext : « Suivant » exécute directement onNext + avance
    // (chemin déterministe, sans dépendre d'un événement synthétique sur un
    // composant qui peut se re-render).
    // Étape « clic sur cible » sans onNext : « Suivant » simule le vrai clic.
    if (!step.onNext && stepClickToAdvance(step) && !step.advanceWhen && active.currentTarget) {
      // Si seul un sous-élément est cliquable (allowWithin), c'est LUI qu'on
      // simule (ex. l'étoile IA dans la cellule), pas la cible entière.
      const clickTarget = step.allowWithin
        ? active.currentTarget.querySelector(step.allowWithin) || active.currentTarget
        : active.currentTarget;
      simulateClick(clickTarget);
      return;
    }
    Api.next();
  }

  // Simule un vrai clic utilisateur : certains éléments réagissent à `mousedown`
  // (ex. « Ajouter une formule ») et pas au seul `click()`. On émet la séquence
  // complète pour que « Suivant » déclenche le même comportement qu'un clic réel.
  function simulateClick(elTarget) {
    if (!elTarget) return;
    ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach((type) => {
      try {
        elTarget.dispatchEvent(
          new MouseEvent(type, { bubbles: true, cancelable: true, view: window }),
        );
      } catch {
        /* PointerEvent/MouseEvent indispo : on ignore ce type */
      }
    });
  }

  function onTargetClick() {
    if (!active) return;
    const step = active.config.steps[active.stepIndex];
    if (step.waitFor) {
      waitForTarget(resolveSelector(step.waitFor), step.waitTimeout || 2000).then((next) => {
        if (next && active && active.stepIndex === active.config.steps.indexOf(step)) {
          Api.next();
        }
      });
    } else {
      setTimeout(() => Api.next(), 60);
    }
  }

  function cleanupTargetHighlight() {
    if (!active) return;
    if (active.currentShowCleanup) {
      try { active.currentShowCleanup(); } catch { /* noop */ }
      active.currentShowCleanup = null;
    }
    if (active.advancePoll) {
      clearInterval(active.advancePoll);
      active.advancePoll = null;
    }
    if (active.currentTarget) {
      active.currentTarget.classList.remove('tour-target--active', 'is-bouncing');
      active.currentTarget.style.zIndex = active.targetPrevZ || '';
      active.currentTarget.style.position = active.targetPrevPos || '';
      if (active.currentClickHandler) {
        active.currentTarget.removeEventListener('click', active.currentClickHandler, { capture: true });
      }
      active.currentTarget = null;
      active.currentClickHandler = null;
    }
    resetOverlayClip();
  }

  // Numérotation "affichée" : une étape avec `groupWithPrevious: true` ne compte
  // pas comme une nouvelle étape visible — elle partage le numéro de la précédente
  // (ex. surligner la partie gauche puis droite d'une même ligne = 1 seul numéro).
  function displayInfo() {
    const steps = active.config.steps;
    const idx = active.stepIndex;
    let total = 0;
    let current = 0;
    for (let i = 0; i < steps.length; i++) {
      if (!steps[i].groupWithPrevious) total++;
      if (i <= idx && !steps[i].groupWithPrevious) current++;
    }
    return { current, total };
  }

  function updateRail() {
    const cfg = active.config;
    const idx = active.stepIndex;
    const step = cfg.steps[idx];
    const { current, total } = displayInfo();
    const rail = active.nodes.rail;

    rail.querySelector('.tour-rail__step-label').textContent = `Étape ${current} sur ${total}`;
    rail.querySelector('.tour-rail__card-title').textContent = step.title;
    rail.querySelector('.tour-rail__card-body').textContent = step.body;

    const progress = rail.querySelector('.tour-rail__progress');
    progress.innerHTML = '';
    for (let i = 0; i < total; i++) {
      const seg = el('div', 'tour-rail__progress-seg');
      if (i < current - 1) seg.classList.add('is-done');
      else if (i === current - 1) seg.classList.add('is-current');
      progress.appendChild(seg);
    }
  }

  function updateBubble() {
    const cfg = active.config;
    const idx = active.stepIndex;
    const step = cfg.steps[idx];
    const { current, total } = displayInfo();
    const b = active.nodes.bubble;

    b.querySelector('.tour-bubble__badge').textContent = step.badge || `Étape ${current} / ${total}`;
    b.querySelector('.tour-bubble__title').textContent = step.title;
    b.querySelector('.tour-bubble__body').textContent = step.body;

    const dots = b.querySelector('.tour-bubble__dots');
    dots.innerHTML = '';
    for (let i = 0; i < total; i++) {
      const d = el('span', 'tour-bubble__dot');
      if (i < current - 1) d.classList.add('is-done');
      else if (i === current - 1) d.classList.add('is-current');
      dots.appendChild(d);
    }

    // Bouton « Suivant » (« Terminer » au dernier pas) affiché sur toutes les
    // étapes : cliquer dessus fait la même chose que cliquer sur la cible.
    const nextBtn = b.querySelector('.tour-bubble__next');
    if (nextBtn) {
      // Étape « advanceWhen » : on avance automatiquement à l'apparition d'un
      // élément (ex. le panneau Détails après création de la formule), pas de
      // bouton « Suivant » — l'utilisateur doit accomplir l'action réelle.
      nextBtn.style.display = step.advanceWhen ? 'none' : '';
      nextBtn.textContent = idx >= cfg.steps.length - 1 ? 'Terminer' : 'Suivant';
    }

    b.setAttribute('data-placement', step.placement || 'bottom-start');
    requestAnimationFrame(() => b.classList.add('is-shown'));
  }

  function positionBubble() {
    if (!active || !active.currentTarget) return;
    const b = active.nodes.bubble;
    const step = active.config.steps[active.stepIndex];
    const placement = step.placement || 'bottom-start';

    const t = active.currentTarget.getBoundingClientRect();
    const bw = b.offsetWidth || 280;
    const bh = b.offsetHeight || 160;
    const gap = 8;
    const pad = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top;
    let left;
    if (placement.startsWith('bottom')) {
      top = t.bottom + gap;
      if (placement === 'bottom-end') left = t.right - bw;
      else if (placement === 'bottom') left = t.left + (t.width - bw) / 2;
      else left = t.left;
    } else if (placement.startsWith('top')) {
      top = t.top - bh - gap;
      if (placement === 'top-end') left = t.right - bw;
      else if (placement === 'top') left = t.left + (t.width - bw) / 2;
      else left = t.left;
    } else if (placement.startsWith('right')) {
      left = t.right + gap;
      top = (placement === 'right-start') ? t.top : (t.top + (t.height - bh) / 2);
    } else if (placement.startsWith('left')) {
      left = t.left - bw - gap;
      top = (placement === 'left-start') ? t.top : (t.top + (t.height - bh) / 2);
    }

    left = Math.max(pad, Math.min(left, vw - bw - pad));
    top = Math.max(pad, Math.min(top, vh - bh - pad));

    b.style.top = `${top}px`;
    b.style.left = `${left}px`;

    // Aligne le caret de la bulle sur le centre de la cible — y compris quand
    // la bulle a été recalée horizontalement pour rester dans l'écran.
    const caret = b.querySelector('.tour-bubble__caret');
    if (caret) {
      if (placement.startsWith('right') || placement.startsWith('left')) {
        const targetCenterY = t.top + t.height / 2;
        const caretY = targetCenterY - top - 6; // 6 = demi-hauteur du caret (12px)
        caret.style.top = `${Math.max(8, Math.min(caretY, bh - 20))}px`;
        caret.style.left = '';
      } else {
        const targetCenterX = t.left + t.width / 2;
        const caretX = targetCenterX - left - 6; // 6 = demi-largeur du caret (12px)
        caret.style.left = `${Math.max(12, Math.min(caretX, bw - 24))}px`;
        caret.style.top = '';
      }
    }
  }

  function celebrate() {
    if (!active) return;
    const cfg = active.config;
    const cel = cfg.celebration || {};
    const origin = active.origin;
    // Vraie fin du tuto (pas un opt-out) → notifie l'app pour persister la
    // complétion en DB (POST …/complete). Écouté par tours/completion-sync.
    try { appBus.$emit('tour:completed', cfg.id); } catch { /* noop */ }

    cleanupTargetHighlight();
    active.nodes.bubble.style.opacity = '0';
    setTimeout(() => active.nodes.bubble && active.nodes.bubble.remove(), 220);

    // Origine /guide → on revient sur /guide (qui reflète la complétion),
    // après le nettoyage du tuto.
    if (origin === 'guide') {
      if (cfg.onExit) { try { cfg.onExit(); } catch { /* noop */ } }
      Api.exit({ silent: true });
      setTimeout(() => {
        try { router.push('/guide'); }
        catch { window.location.href = '/guide'; }
      }, 200);
      return;
    }

    // Mode modale centrée : transition propre (pop-up blanche sur fond grisé),
    // façon pop-up de l'app. On garde l'overlay pour le fond grisé.
    if (cel.modal) {
      showFinalModal(cel.modal);
      return;
    }

    active.nodes.overlay.style.opacity = '0';
    setTimeout(() => active.nodes.overlay && active.nodes.overlay.remove(), 220);

    // Écran de fin "prise en main" : réussite + checklist + prochaine étape,
    // réinjecté dans le RAIL GAUCHE. Déclenché par un actionPanel, le flag
    // drawer, OU une origine « recap » (on repart toujours du recap à jour).
    if (origin === 'recap' || cel.actionPanel || cel.drawer) {
      // La complétion est persistée côté back (tour:completed → completion-sync),
      // qui recharge et re-pousse la checklist puis rafraîchit cet écran.
      // Fin normale du tuto : on déclenche le nettoyage (ex. bac à sable) avant
      // d'afficher les raccourcis.
      if (cfg.onExit) { try { cfg.onExit(); } catch { /* noop */ } }
      renderEndScreen(cel);
      return;
    }

    const toast = el('div', 'tour-toast');
    toast.innerHTML = `
      <div class="tour-toast__icon"><span class="material-symbols-outlined">check</span></div>
      <div class="tour-toast__content">
        <div class="tour-toast__title">${cel.title || 'Module terminé'}</div>
        <div class="tour-toast__body">${cel.body || ''}</div>
      </div>
      <button class="tour-toast__close" type="button" aria-label="Fermer">×</button>
    `;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-shown'));

    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      toast.classList.remove('is-shown');
      setTimeout(() => toast.remove(), 240);
      // Fin normale du tuto (toast) : on déclenche le nettoyage AVANT la sortie
      // silencieuse (exit silencieux ne relance pas onExit). Sans ça, un cleanup
      // (ex. suppression du scénario de démo) ne tournerait qu'au « Quitter ».
      if (cfg.onExit) { try { cfg.onExit(); } catch { /* noop */ } }
      Api.exit({ silent: true });
      if (cel.returnTo) {
        setTimeout(() => { window.location.href = cel.returnTo; }, 260);
      } else if (cel.followup) {
        // Une fois le bandeau aubergine retiré et la sidenav revenue,
        // on affiche une bulle de follow-up pour inviter l'utilisateur à
        // explorer ou retourner dans Modules.
        setTimeout(() => showFollowup(cel.followup), 400);
      }
    };
    toast.querySelector('.tour-toast__close').addEventListener('click', close);
    setTimeout(close, 3500);
  }


  // Écran de fin "prise en main" : on réinjecte dans le RAIL GAUCHE déjà affiché
  // (continuité, pas de nouveau panneau) un état de réussite, une checklist de
  // progression du parcours, et les prochaines étapes.
  function renderEndScreen(cel) {
    if (!active || !active.nodes || !active.nodes.rail) return;
    const rail = active.nodes.rail;
    // Sort de l'état "intro" éventuel et débloque les clics : le dashboard
    // redevient interactif (seul le rail reste).
    rail.classList.remove('tour-rail--intro');
    // Élargit le rail pour l'écran de fin (la largeur est posée inline au montage,
    // on l'écrase ici avec une transition douce).
    rail.style.transition = 'width 280ms cubic-bezier(0.2, 0, 0, 1)';
    rail.style.width = '320px';
    if (active.nodes.fab) { active.nodes.fab.remove(); active.nodes.fab = null; }
    document.removeEventListener('click', blockOutsideClick, { capture: true });
    document.removeEventListener('mousedown', blockOutsideClick, { capture: true });
    document.removeEventListener('pointerdown', blockOutsideClick, { capture: true });

    // Mémorise la config de l'écran de fin pour pouvoir le re-render quand le
    // back renvoie la complétion à jour (cf. Api.refreshEnd / completion-sync).
    active.endCel = cel;
    // Source de la checklist : le back (getStartOnboarding, via setOnboardingSteps).
    // Sinon le socle en dur (preview/debug). La complétion vient UNIQUEMENT de la
    // DB (step.completed) — plus aucun état local.
    const STEPS = ONBOARDING_STEPS_OVERRIDE || ONBOARDING_STEPS;
    const isStepDone = (s) => s.completed === true;
    const done = STEPS.filter(isStepDone).length;
    const total = STEPS.length;
    const pct = total ? Math.round((done / total) * 100) : 0;
    // Parcours "Prise en main" bouclé → on propose le module SUIVANT.
    const journeyComplete = total > 0 && done >= total;

    const stepsHtml = STEPS.map((s) => {
      const isDone = isStepDone(s);
      return `
        <li class="tour-drawer__step ${isDone ? 'is-done' : ''}">
          <span class="tour-drawer__step-ico material-symbols-outlined">${isDone ? 'check_circle' : 'radio_button_unchecked'}</span>
          <span class="tour-drawer__step-label">${nbsp(s.label)}</span>
        </li>`;
    }).join('');

    // Socle en cours → « Prochaine étape » = 1er tuto non fait du socle (dérivé
    // automatiquement, plus besoin de le coder dans chaque célébration).
    const nextStep = journeyComplete
      ? null
      : STEPS.find((s) => !isStepDone(s));
    const nextStepHtml = nextStep ? `
      <button class="tour-drawer__module" type="button" data-next-step>
        <span class="tour-drawer__module-text">
          <span class="tour-drawer__module-label">${nbsp(nextStep.label)}</span>
        </span>
        <span class="tour-drawer__module-arrow material-symbols-outlined">arrow_forward</span>
      </button>` : '';

    // Modules proposés après le socle : on n'affiche que ceux dont le tuto est
    // enregistré (les modules à venir — scenarios, taux-de-change, alertes… —
    // restent masqués tant que leur tuto n'existe pas).
    const modules = MODULES.filter((m) => m && tours.has(m.id));
    const modulesHtml = modules.map((m, i) => `
      <button class="tour-drawer__module" type="button" data-module-index="${i}">
        <span class="tour-drawer__module-ico material-symbols-outlined">${m.icon || 'bolt'}</span>
        <span class="tour-drawer__module-text">
          <span class="tour-drawer__module-label">${nbsp(m.label || '')}</span>
          ${m.body ? `<span class="tour-drawer__module-body">${nbsp(m.body)}</span>` : ''}
        </span>
        <span class="tour-drawer__module-arrow material-symbols-outlined">arrow_forward</span>
      </button>
    `).join('');

    // Socle bouclé ET aucun module à proposer → état de fin : on affiche un CTA
    // primaire « C'est parti » (le lien « Plus tard » devient alors redondant).
    const socleDone = journeyComplete && !modulesHtml;

    // Un seul titre : dès que le socle est bouclé on l'unifie sur « Prise en main
    // terminée » (qu'il y ait des modules à proposer ou non) — évite la
    // redondance de titres avec la célébration du dernier tuto.
    const heroTitle = journeyComplete ? 'Prise en main terminée' : (cel.title || 'Bravo !');
    const heroBody = journeyComplete
      ? 'Vous connaissez l’essentiel d’Okimia. Relancez un guide quand vous le souhaitez depuis « Modules ».'
      : cel.body;

    // Le rail devient le conteneur de l'écran de fin (styles .tour-drawer).
    rail.classList.add('tour-drawer', 'tour-drawer--rail');
    rail.innerHTML = `
      <div class="tour-drawer__success">
        <div class="tour-drawer__check"><span class="material-symbols-outlined">check</span></div>
        <div class="tour-drawer__title">${nbsp(heroTitle)}</div>
        ${heroBody ? `<div class="tour-drawer__body">${nbsp(heroBody)}</div>` : ''}
      </div>
      <div class="tour-drawer__progress">
        <div class="tour-drawer__progress-head">
          <span>Prise en main</span><span class="tour-drawer__progress-count">${done}/${total}</span>
        </div>
        <div class="tour-drawer__bar"><i style="width:${pct}%"></i></div>
        <ul class="tour-drawer__list">${stepsHtml}</ul>
      </div>
      ${journeyComplete ? (modulesHtml ? `
        <div class="tour-drawer__next-head">
          <span class="material-symbols-outlined">auto_stories</span>
          Pour aller plus loin
        </div>
        <div class="tour-drawer__actions">${modulesHtml}</div>
      ` : `
        <button class="tour-drawer__finish-cta" type="button" data-finish>C’est parti</button>
      `) : (nextStepHtml ? `
        <div class="tour-drawer__next-head">
          <span class="material-symbols-outlined">arrow_downward</span>
          Prochaine étape
        </div>
        <div class="tour-drawer__actions">${nextStepHtml}</div>` : '')}
      ${socleDone ? '' : '<button class="tour-drawer__dismiss" type="button">Plus tard</button>'}
    `;

    const dismissBtn = rail.querySelector('.tour-drawer__dismiss');
    if (dismissBtn) dismissBtn.addEventListener('click', () => {
      // « Plus tard » → met l'auto-ouverture en pause (snooze), sans désactiver
      // les guides (ça, c'est « Désactiver » → dismissed back). Cf. tours/onboarding-recap.
      try { appBus.$emit('tour:snooze'); } catch { /* noop */ }
      Api.exit({ silent: true });
    });
    const finishBtn = rail.querySelector('[data-finish]');
    if (finishBtn) finishBtn.addEventListener('click', () => Api.exit({ silent: true }));
    // Lancé depuis le recap → on marque l'origine (&from=recap) pour qu'à la fin
    // du tuto on rouvre le recap à jour.
    const launchFromRecap = (id, page) => {
      const p = page || '/dashboard';
      const sep = p.includes('?') ? '&' : '?';
      window.location.href = `${p}${sep}tour=${encodeURIComponent(id)}&from=recap`;
    };
    const nextStepBtn = rail.querySelector('[data-next-step]');
    if (nextStepBtn && nextStep) {
      nextStepBtn.addEventListener('click', () => launchFromRecap(nextStep.id, nextStep.page));
    }
    rail.querySelectorAll('[data-module-index]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const m = modules[Number(btn.getAttribute('data-module-index'))];
        if (m) launchFromRecap(m.id, m.page);
      });
    });
  }

  function showFollowup(fu) {
    const bubble = el('div', 'tour-followup');
    bubble.innerHTML = `
      <div class="tour-followup__icon"><span class="material-symbols-outlined">rocket_launch</span></div>
      <div class="tour-followup__content">
        <div class="tour-followup__title">${fu.title || ''}</div>
        <div class="tour-followup__body">${fu.body || ''}</div>
      </div>
      <button class="tour-followup__close" type="button" aria-label="Fermer">×</button>
      <span class="tour-followup__dot" aria-hidden="true"></span>
    `;
    document.body.appendChild(bubble);
    requestAnimationFrame(() => bubble.classList.add('is-shown'));
    const dismiss = () => {
      bubble.classList.remove('is-shown');
      setTimeout(() => bubble.remove(), 260);
    };
    bubble.querySelector('.tour-followup__close').addEventListener('click', dismiss);
    setTimeout(dismiss, 7000);
  }

  function el(tag, className) {
    const e = document.createElement(tag);
    if (className) e.className = className;
    return e;
  }

  function waitForTarget(selector, timeoutMs = 1500) {
    return new Promise((resolve) => {
      const existing = document.querySelector(selector);
      if (existing) return resolve(existing);
      const start = Date.now();
      const iv = setInterval(() => {
        const found = document.querySelector(selector);
        if (found) { clearInterval(iv); resolve(found); }
        else if (Date.now() - start > timeoutMs) { clearInterval(iv); resolve(null); }
      }, 50);
    });
  }

  // Comme waitForTarget mais re-résout une cible dynamique (fonction) à chaque
  // tick : indispensable quand le sélecteur dépend d'un élément pas encore monté
  // (renverrait null au 1er essai). Accepte aussi une string fixe.
  // isCurrent : prédicat optionnel — tant qu'il renvoie false, on arrête net le
  // poll (résout null) pour ne pas laisser un tick d'un run précédent continuer
  // à résoudre la cible (et, via une cible-fonction, déclencher des effets de
  // bord comme le seed de la facture de démo).
  function waitForResolvedTarget(rawTarget, timeoutMs = 1500, isCurrent = null) {
    return new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        if (isCurrent && !isCurrent()) return resolve(null);
        const sel = resolveSelector(rawTarget);
        const found = sel ? document.querySelector(sel) : null;
        if (found) return resolve(found);
        if (Date.now() - start > timeoutMs) return resolve(null);
        setTimeout(tick, 50);
      };
      tick();
    });
  }

  window.FygrTour = Api;
})();
